import React, { useState, useEffect } from 'react';
import axios from 'axios';

const COLORS = {
  primary: '#0760ad',
  secondary: '#098a48',
  tertiary: '#118a94',
  white: '#FFFFFF',
  darkText: '#1a365d'
};

const MessageModal = ({ isOpen, onClose, initialData }) => {
  const [messageData, setMessageData] = useState({
    heading: "",
    subheading: "",
    message: "",
    chairman: {
      name: "",
      position: "",
      image: "",
      signature: ""
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadType, setUploadType] = useState('');

  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/about/message/`);
          if (response.ok) {
            const data = await response.json();
            setMessageData(data);
          }
        } catch (error) {
          console.error('Error fetching message data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setMessageData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMessageData(prev => ({ ...prev, [name]: value }));
  };

  const handleChairmanChange = (field, value) => {
    setMessageData(prev => ({
      ...prev,
      chairman: {
        ...prev.chairman,
        [field]: value
      }
    }));
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', 'message_images');

    try {
      setIsUploading(true);
      setUploadType(type);
      setUploadProgress(0);

      const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      const imageUrl = response.data.image?.url || response.data.image;
      if (imageUrl) {
        if (type === 'chairman') {
          handleChairmanChange('image', imageUrl);
        } else if (type === 'signature') {
          handleChairmanChange('signature', imageUrl);
        }
      } else {
        throw new Error('Invalid image URL in response');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadType('');
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/about/message/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(messageData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        onClose();
      } else {
        console.error('Failed to update message data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating message data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading message data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-5xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-4">Edit Chairman's Message</h3>
        
        <div className="overflow-y-auto max-h-[70vh] pr-2 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Heading</span>
              </label>
              <input 
                type="text" 
                name="heading"
                value={messageData.heading || ''} 
                onChange={handleChange} 
                className="input input-bordered w-full" 
              />
            </div>
            
            <div className="form-control">
              <label className="label">
                <span className="label-text">Subheading</span>
              </label>
              <input 
                type="text" 
                name="subheading"
                value={messageData.subheading || ''} 
                onChange={handleChange} 
                className="input input-bordered w-full" 
              />
            </div>
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text">Message Content</span>
            </label>
            <textarea 
              name="message"
              value={messageData.message || ''} 
              onChange={handleChange} 
              className="textarea textarea-bordered w-full h-48" 
              placeholder="Enter the chairman's message (supports line breaks)"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Chairman's Name</span>
              </label>
              <input 
                type="text" 
                value={messageData.chairman?.name || ''} 
                onChange={(e) => handleChairmanChange('name', e.target.value)} 
                className="input input-bordered w-full" 
              />
            </div>
            
            <div className="form-control">
              <label className="label">
                <span className="label-text">Chairman's Position</span>
              </label>
              <input 
                type="text" 
                value={messageData.chairman?.position || ''} 
                onChange={(e) => handleChairmanChange('position', e.target.value)} 
                className="input input-bordered w-full" 
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Chairman's Photo</span>
              </label>
              <div className="flex items-center gap-2">
                {messageData.chairman?.image && (
                  <div className="avatar">
                    <div className="w-12 rounded">
                      <img src={messageData.chairman.image} alt="Chairman preview" />
                    </div>
                  </div>
                )}
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'chairman')}
                  className="file-input file-input-bordered w-full max-w-xs"
                  disabled={isUploading && uploadType === 'chairman'}
                />
                {isUploading && uploadType === 'chairman' && (
                  <progress 
                    className="progress progress-primary w-20" 
                    value={uploadProgress} 
                    max="100"
                  ></progress>
                )}
              </div>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Chairman's Signature</span>
              </label>
              <div className="flex items-center gap-2">
                {messageData.chairman?.signature && (
                  <div className="avatar">
                    <div className="w-12 rounded">
                      <img src={messageData.chairman.signature} alt="Signature preview" />
                    </div>
                  </div>
                )}
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'signature')}
                  className="file-input file-input-bordered w-full max-w-xs"
                  disabled={isUploading && uploadType === 'signature'}
                />
                {isUploading && uploadType === 'signature' && (
                  <progress 
                    className="progress progress-primary w-20" 
                    value={uploadProgress} 
                    max="100"
                  ></progress>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-action mt-4">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default MessageModal;